import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { toast } from "react-toastify";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    try {
      await addDoc(collection(db, "contacts"), {
        ...form,
        timestamp: serverTimestamp(),
      });
      toast.success("✅ Message sent successfully!");
      setStatus("Your message has been sent successfully!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus("❌ Failed to send message. Please try again.");
    }

    setLoading(false);
  };

  return (
    <Wrapper>
      {loading && (
        <LoadingOverlay>
          <Spinner />
        </LoadingOverlay>
      )}

      <Card>
        <Title>Contact Us</Title>
        <Description>
          Have a question, feedback, or collaboration idea? Fill out the form
          below — we’ll get back to you soon.
        </Description>

        <Form onSubmit={handleSubmit}>
          <Input
            type="text"
            name="name"
            placeholder="Your Name"
            value={form.name}
            onChange={handleChange}
            required
          />
          <Input
            type="email"
            name="email"
            placeholder="Your Email"
            value={form.email}
            onChange={handleChange}
            required
          />
          <TextArea
            rows="5"
            name="message"
            placeholder="Your Message"
            value={form.message}
            onChange={handleChange}
            required
          />
          <Button type="submit" disabled={loading}>
            {loading ? "Sending..." : "Send Message"}
          </Button>
        </Form>

        {status && <StatusMessage>{status}</StatusMessage>}

        <InfoBox>
          <InfoItem>
            📧 <span>contact@solvemycodeissue.com</span>
          </InfoItem>
          <InfoItem>
            📍 <span>Noida, India</span>
          </InfoItem>
        </InfoBox>
      </Card>
    </Wrapper>
  );
};

export default ContactPage;

/* ---------------- Styled Components ---------------- */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px);}
  to { opacity: 1; transform: translateY(0);}
`;

const Wrapper = styled.div`
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
`;

const Card = styled.div`
  max-width: 700px;
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(155, 92, 255, 0.2);
  padding: 2.5rem 2rem;
  border-radius: 20px;
  color: #fff;
  text-align: center;
  box-shadow: 0 0 30px rgba(155, 92, 255, 0.15);
  animation: ${fadeIn} 0.6s ease;

  @media (max-width: 768px) {
    padding: 1.8rem 1.2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.4rem;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #a855f7, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Description = styled.p`
  color: #ccc;
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
`;

const Input = styled.input`
  padding: 0.9rem 1rem;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 1rem;
  transition: 0.25s ease;
  &:focus {
    border-color: rgba(155, 92, 255, 0.6);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.3);
    outline: none;
  }
`;

const TextArea = styled.textarea`
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid rgba(155, 92, 255, 0.25);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 1rem;
  resize: none;
  transition: 0.25s ease;
  &:focus {
    border-color: rgba(155, 92, 255, 0.6);
    box-shadow: 0 0 10px rgba(155, 92, 255, 0.3);
    outline: none;
  }
`;

const Button = styled.button`
  margin-top: 10px;
  padding: 0.9rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  cursor: pointer;
  transition: 0.3s ease;
  box-shadow: 0 0 0 rgba(155, 92, 255, 0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 18px rgba(155, 92, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  font-size: 1rem;
  color: #c59fff;
  text-align: center;
`;

const InfoBox = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const InfoItem = styled.div`
  font-size: 1.1rem;
  color: #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
  span {
    margin-left: 0.5rem;
    color: #fff;
    font-weight: 500;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const spin = keyframes`
  0% { transform: rotate(0deg);}
  100% { transform: rotate(360deg);}
`;

const Spinner = styled.div`
  border: 6px solid #eee;
  border-top: 6px solid #a855f7;
  border-radius: 50%;
  width: 65px;
  height: 65px;
  animation: ${spin} 1s linear infinite;
`;
